import { useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  MANUAL_FEATURES,
  type ManualFeatureType,
} from '@/config/manualCadFeatures';
import { useManualCadOptional } from '@/contexts/manual-cad-context';
import type { ManualFeatureInput } from '@/services/manualCad';
import { ManualCadFeatureForm } from './ManualCadFeatureForm';
import { cn } from '@/lib/utils';

const BTN_BASE =
  'rounded-lg border px-3 py-2 text-xs font-medium transition-colors';
const BTN_ENABLED =
  'border-[#737373] bg-telekinesis-bg-dark text-telekinesis-neutral-50 hover:border-[#107d40] hover:bg-[#0f3d24]';
const BTN_DISABLED =
  'cursor-default border-[#737373] bg-telekinesis-bg-dark text-telekinesis-text-secondary opacity-50';

/**
 * The FreeCAD-style feature bar shown in the toolbar's pill row when
 * `cadMode === 'manual'`. Each enabled feature opens a parameter popover
 * (ManualCadFeatureForm) — click Pad → set length + pick the sketch → Add.
 * Availability follows the feature-history rules: Pad/Pocket need a Sketch,
 * Fillet/Chamfer need a solid, Boolean is deferred.
 * See docs/freecad-manual-cad-integration-plan.md.
 */
export function ManualCadFeatureBar() {
  const mc = useManualCadOptional();
  const features = mc?.features ?? [];
  const [openType, setOpenType] = useState<ManualFeatureType | null>(null);

  const sketches = features.filter((f) => f.type === 'Sketch');
  const hasSketch = sketches.length > 0;
  const hasSolid = features.some((f) => f.type === 'Pad');

  const availability = (
    type: ManualFeatureType,
  ): { enabled: boolean; reason?: string } => {
    if (mc?.busy) return { enabled: false, reason: 'Working…' };
    switch (type) {
      case 'Sketch':
        return { enabled: true };
      case 'Pad':
      case 'Pocket':
        return hasSketch
          ? { enabled: true }
          : { enabled: false, reason: 'Add a Sketch first' };
      case 'Fillet':
      case 'Chamfer':
        return hasSolid
          ? { enabled: true }
          : { enabled: false, reason: 'Needs a solid (Pad) first' };
      case 'Boolean':
        return { enabled: false, reason: 'Coming soon' };
    }
  };

  const submit = (input: ManualFeatureInput) => {
    if (mc) void mc.addFeature(input);
    setOpenType(null);
  };

  return (
    <div className="flex items-end gap-2">
      {MANUAL_FEATURES.map((feat) => {
        const { enabled, reason } = availability(feat.type);

        if (!enabled) {
          return (
            <Tooltip key={feat.type}>
              {/* Focusable span so the tooltip fires on a disabled button. */}
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex">
                  <button
                    type="button"
                    disabled
                    className={cn(BTN_BASE, BTN_DISABLED)}
                  >
                    {feat.label}
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {reason ?? feat.hint}
              </TooltipContent>
            </Tooltip>
          );
        }

        return (
          <Popover
            key={feat.type}
            open={openType === feat.type}
            onOpenChange={(o) => setOpenType(o ? feat.type : null)}
          >
            <PopoverTrigger asChild>
              <button type="button" className={cn(BTN_BASE, BTN_ENABLED)}>
                {feat.label}
              </button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="start" className="w-auto p-3">
              <ManualCadFeatureForm
                type={feat.type}
                sketches={sketches}
                disabled={mc?.busy}
                onSubmit={submit}
              />
            </PopoverContent>
          </Popover>
        );
      })}
    </div>
  );
}
